import {
  awayKindsOf,
  cellKey,
  daysInMonth,
  dutyKindsOf,
  iso,
  isFemale,
  monthNames,
  monthTitle,
  searchKey,
  commandOrder,
  type IsoDate,
  type CheckResponse,
  type MarkOp,
  type Person,
  type RecentMarksResponse,
} from '@kancelaria/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, errorText } from '../api/client.js';
import { Grid } from '../components/Grid.js';
import { CheckModal, checkMap, type CheckResult } from '../components/modals/CheckModal.js';
import { LeaveReportsGrid, REPORT_KINDS, type ReportKind } from '../components/LeaveReportsGrid.js';
import { MarkStyles } from '../components/MarkStyles.js';
import { CanteenModal } from '../components/modals/CanteenModal.js';
import { DutyTodayModal } from '../components/modals/DutyTodayModal.js';
import { FillDialog } from '../components/modals/FillDialog.js';
import { TransferDialog } from '../components/modals/TransferDialog.js';
import { ReportModal } from '../components/modals/ReportModal.js';
import { HintIcon, LockIcon, SearchIcon, TouchIcon } from '../components/ui.js';
import { HistoryPanel, type HistoryHighlight } from '../components/HistoryPanel.js';
import { MobileMenu } from '../components/MobileMenu.js';
import { Segments } from '../components/Segments.js';
import { usePhone } from '../hooks/useMedia.js';
import { useMenu } from '../hooks/useMenu.js';
import { useToolbarFit } from '../hooks/useToolbarFit.js';
import { useCan, useData } from '../store/data.js';
import { useLock } from '../store/lock.js';
import { useMarks } from '../store/marks.js';
import { useMonth } from '../store/month.js';
import { toast, useUi } from '../store/ui.js';

type Dialog = 'fill' | 'transfer' | 'canteen' | 'report' | 'dutyToday' | 'check' | null;

/**
 * Яку таблицю показує сторінка: харчування, наряди, відпустки й ЛР чи рапорти
 * на звільнення. Рапорти — не позначки, у них своя сітка й свої дані.
 */
export type TableMode = 'meals' | 'duty' | 'away' | 'reports';

/** Колонки та лінійки, які можна сховати з меню «Вигляд». */
export interface ViewOptions {
  numbers: boolean;
  rank: boolean;
  unit: boolean;
  /** Підсвітка рядка й доби під курсором. */
  crosshair: boolean;
  /** Межі діб кольором акценту, а не сірим. */
  dayLines: boolean;
  totals: boolean;
  /** Нижня смуга з підсумками «Компенсація» та «На харчуванні». */
  footer: boolean;
  /** Підсвітити тих, у кого нарядів за показаний період найменше. */
  fewestDuties: boolean;
}

const DEFAULT_VIEW: ViewOptions = {
  numbers: true,
  rank: false,
  unit: true,
  crosshair: true,
  dayLines: false,
  totals: true,
  footer: true,
  fewestDuties: false,
};

const readView = (): Partial<ViewOptions> => {
  try {
    return JSON.parse(localStorage.getItem('kanc.tableView') ?? '{}') as Partial<ViewOptions>;
  } catch {
    return {};
  }
};

const VIEW_ITEMS: Array<{ key: keyof ViewOptions; label: string }> = [
  { key: 'numbers', label: 'Нумерація' },
  { key: 'rank', label: 'Звання' },
  { key: 'unit', label: 'Група' },
  { key: 'crosshair', label: 'Лінійка під курсором' },
  { key: 'dayLines', label: 'Підсвітити межі діб' },
  { key: 'totals', label: 'Підсумкові стовпчики' },
  { key: 'footer', label: 'Нижня смуга підсумків' },
  { key: 'fewestDuties', label: 'Підсвітити, у кого нарядів найменше' },
];

/**
 * Прокрутити таблицю так, щоб клітинка стала посередині видимої частини.
 *
 * `scrollIntoView` тут не годиться: він вважає видимим усе вікно таблиці, а
 * зліва його закривають липкі ПІБ і група, справа — підсумки, згори шапка
 * днів, знизу рядки підсумків. Клітинка «в полі зору» лишалася під ними.
 * Тож рахуємо вільний прямокутник самі й рухаємо лише тоді, коли клітинка
 * в нього не вміщається. Без плавності: у фоновій вкладці плавна прокрутка
 * не доходить до кінця, а підсвітку й так видно.
 */
function scrollCellIntoView(cell: HTMLElement): void {
  const wrap = cell.closest<HTMLElement>('.grid-wrap');
  if (!wrap) return;
  const box = wrap.getBoundingClientRect();
  const c = cell.getBoundingClientRect();
  let left = box.left;
  let right = box.right;
  for (const s of cell.parentElement?.querySelectorAll<HTMLElement>('.sticky, .c-tot, .c-sum') ?? []) {
    const r = s.getBoundingClientRect();
    if (getComputedStyle(s).left !== 'auto') left = Math.max(left, r.right);
    else right = Math.min(right, r.left);
  }
  const top = Math.max(box.top, wrap.querySelector('thead')?.getBoundingClientRect().bottom ?? box.top);
  const bottom = Math.min(box.bottom, wrap.querySelector('tfoot')?.getBoundingClientRect().top ?? box.bottom);
  const inside = c.left >= left && c.right <= right && c.top >= top && c.bottom <= bottom;
  if (inside) return;
  wrap.scrollBy({
    left: c.left + c.width / 2 - (left + right) / 2,
    top: c.top + c.height / 2 - (top + bottom) / 2,
  });
}

/** Пауза, щоб встигнути добрати другу літеру («к» → «кн»). */
const MARK_CHORD_MS = 350;

/**
 * Яку позначку вмикає набрана літера.
 *
 * Одна літера вмикає одразу. Якщо є довша («к» і «кн») — коротка стає
 * одразу, а друга літера протягом паузи перемикає на довгу.
 */
function matchMarkKey(
  types: Array<{ code: string; letter: string }>,
  seq: string,
): { code: string | null; wait: boolean } {
  const s = seq.toLowerCase();
  const hits = types.filter(t => t.letter.toLowerCase().startsWith(s));
  if (!hits.length) return { code: null, wait: false };
  const exact = hits.find(t => t.letter.toLowerCase() === s);
  const canGrow = hits.some(t => t.letter.length > s.length);
  if (canGrow)
    return { code: exact?.code ?? (hits.length === 1 ? hits[0]!.code : null), wait: true };
  return { code: exact?.code ?? hits[0]!.code, wait: false };
}

export function TablePage({ mode }: { mode: TableMode }): ReactNode {
  const { people, units, ranks, positions, session } = useData();
  const settings = session!.org.settings;
  // Беремо зі сховища позначок лише те, чим користуємось. Підписка на весь
  // об'єкт перемальовувала всю сторінку — панель, меню, таблицю — на кожну
  // поставлену клітинку, а їх за протяжку ставлять десятки.
  const markIndex = useMarks(s => s.index);
  const applyMarks = useMarks(s => s.apply);
  const loadMarks = useMarks(s => s.load);
  const undoMarks = useMarks(s => s.undo);
  const redoMarks = useMarks(s => s.redo);
  const syncing = useMarks(s => s.syncing);
  const canEdit = useCan('marks.edit');
  const canDocs = useCan('documents.generate');
  const lockOpen = useLock(s => s.open);
  const setLockOpen = useLock(s => s.setOpen);
  const phone = usePhone();

  const now = new Date();
  const month = useMonth(s => s.month);
  const setMonth = useMonth(s => s.setMonth);
  const [range, setRange] = useState<{ from: number | null; to: number | null }>({
    from: null,
    to: null,
  });
  const [query, setQuery] = useState('');
  const [unitId, setUnitId] = useState('');
  // Кожна позначка ставиться лише у своїй таблиці: наряди — у нарядах,
  // відпустки й ЛР — у відпустках, решта — у харчуванні. У харчування наряди
  // й відпустки потрапляють кнопкою перенесення.
  const dutyCodes = dutyKindsOf(settings);
  const awayCodes = awayKindsOf(settings.markTypes);
  const paletteOf = (m: TableMode): typeof settings.markTypes =>
    settings.markTypes.filter(t =>
      m === 'duty'
        ? dutyCodes.includes(t.code)
        : m === 'away'
          ? awayCodes.includes(t.code)
          : m === 'reports'
            ? false
            : !dutyCodes.includes(t.code) && !awayCodes.includes(t.code),
    );

  const [tool, setTool] = useState(
    () => paletteOf(mode)[0]?.code ?? settings.markTypes[0]?.code ?? 'k',
  );
  const [hintMode, setHintMode] = useState(false);
  const [reportKind, setReportKind] = useState<ReportKind>('leave');
  // Випадковий тап чи клік не має нічого міняти: таблицю частіше гортають і
  // звіряють, ніж правлять. Тож позначки за замовчуванням заблоковані — і на
  // телефоні, і на комп'ютері; замок знімають самі, коли беруться ставити.
  const [markLock, setMarkLock] = useState(true);
  /**
   * Клік по заблокованій таблиці пояснюємо, а не мовчимо — як і замок минулих
   * днів. Протяжка торкається десятка клітинок, тож сповіщення раз на кілька
   * секунд, а не на кожну.
   */
  const lockWarnedAt = useRef(0);
  const warnMarkLock = useCallback((): void => {
    const now = Date.now();
    if (now - lockWarnedAt.current < 3000) return;
    lockWarnedAt.current = now;
    toast(
      phone
        ? 'Позначки заблоковано. Щоб ставити — зніміть блокування кнопкою з рукою внизу'
        : 'Позначки заблоковано. Щоб ставити — зніміть блокування кнопкою з рукою в панелі',
      'err',
    );
  }, [phone]);
  const [monthPop, setMonthPop] = useState(false);
  /** Меню телефона: чи розкрита сітка місяців під стрілками. */
  const [menuGrid, setMenuGrid] = useState(false);
  const [viewPop, setViewPop] = useState(false);
  const [filterPop, setFilterPop] = useState(false);
  // Що показувати в таблиці. Зберігається, бо в кожного діловода свій набір.
  const [view, setView] = useState<ViewOptions>(() => ({ ...DEFAULT_VIEW, ...readView() }));
  const toggleView = (key: keyof ViewOptions): void => {
    const next = { ...view, [key]: !view[key] };
    setView(next);
    localStorage.setItem('kanc.tableView', JSON.stringify(next));
  };
  /** Історія змін відкрита; обрана в ній дія світиться в таблиці. */
  const [history, setHistory] = useState(false);
  const [recentKeys, setRecentKeys] = useState<Set<string>>(() => new Set());
  const [recentGone, setRecentGone] = useState<Map<string, string>>(() => new Map());
  const closeHistory = useCallback((): void => {
    setHistory(false);
    setRecentKeys(new Set());
    setRecentGone(new Map());
  }, []);
  const highlight = useCallback((h: HistoryHighlight): void => {
    setRecentKeys(h.keys);
    setRecentGone(h.gone);
  }, []);
  /** «Перевірка»: людина → `date|meal` → що не сходиться; `null` — вимкнена. */
  const [check, setCheck] = useState<CheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  /** Розбіжності обведені в таблиці. */
  const [checkShown, setCheckShown] = useState(false);
  const issues = useMemo(() => (check && checkShown ? checkMap(check.issues) : null), [check, checkShown]);
  const [popYear, setPopYear] = useState(month.y);
  const [dialog, setDialog] = useState<Dialog>(null);
  const menu = useUi(s => s.menu);
  const setMenu = useUi(s => s.setMenu);
  const [person, setPerson] = useState<Person | null>(null);
  const monthBox = useRef<HTMLDivElement>(null);
  const toolbar = useToolbarFit<HTMLDivElement>();
  const viewBox = useMenu(viewPop, () => setViewPop(false));
  const filterBox = useMenu(filterPop, () => setFilterPop(false));
  const chord = useRef<{ seq: string; timer: ReturnType<typeof setTimeout> | null }>({
    seq: '',
    timer: null,
  });

  const monthFrom = iso(new Date(month.y, month.m, 1));
  const monthTo = iso(new Date(month.y, month.m, daysInMonth(month.y, month.m)));

  useEffect(() => {
    void loadMarks(monthFrom, monthTo);
    setCheck(null);
    setCheckShown(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFrom, monthTo]);

  /** Звірити місяць: сервер віддає лише клітинки з розбіжністю. */
  const runCheck = async (): Promise<void> => {
    setChecking(true);
    try {
      const res = await api.get<CheckResponse>(`/api/documents/audit?from=${monthFrom}&to=${monthTo}`);
      setCheck({ issues: res.issues, at: new Date() });
      setCheckShown(res.issues.length > 0);
    } catch (e) {
      toast(errorText(e), 'err');
    } finally {
      setChecking(false);
    }
  };

  /** Рядок у вікні перевірки: закрити вікно й показати саме цю клітинку. */
  const showIssue = (personId: string, date: IsoDate, meal: number): void => {
    setDialog(null);
    setCheckShown(true);
    requestAnimationFrame(() => {
      const cell = document.querySelector<HTMLElement>(
        `tr[data-person="${personId}"] td.cell[data-d="${date}"][data-meal="${meal}"]`,
      );
      if (cell) scrollCellIntoView(cell);
    });
  };


  // Обрана в історії дія — під очі: таблицю прокручуємо до її першої клітинки.
  // Розміри читаємо вже після того, як панель історії звузила таблицю: ефект
  // іде після запису в DOM, а читання розмірів саме перераховує розкладку.
  useEffect(() => {
    if (!recentKeys.size) return;
    const cell = document.querySelector<HTMLElement>('td.cell.recent');
    if (cell) scrollCellIntoView(cell);
  }, [recentKeys]);

  useEffect(() => {
    if (!monthPop) return;
    const close = (e: MouseEvent): void => {
      if (!monthBox.current?.contains(e.target as Node)) setMonthPop(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [monthPop]);

  /* --------------------------------------------------------- гарячі клавіші */
  useEffect(() => {
    const clearChord = (): void => {
      if (chord.current.timer) clearTimeout(chord.current.timer);
      chord.current = { seq: '', timer: null };
    };

    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (target && (/input|select|textarea/i.test(target.tagName) || target.isContentEditable))
        return;
      if (e.isComposing) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        clearChord();
        if (e.shiftKey) redoMarks();
        else undoMarks();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        clearChord();
        redoMarks();
        return;
      }
      if (mod || e.altKey) return;

      const codes = paletteOf(mode);

      if (!dialog && e.key.length === 1 && !e.repeat) {
        let used = chord.current.seq + e.key;
        let hit = matchMarkKey(codes, used);
        if (!hit.code && !hit.wait && chord.current.seq) {
          clearChord();
          used = e.key;
          hit = matchMarkKey(codes, used);
        }
        if (hit.wait || hit.code) {
          e.preventDefault();
          if (chord.current.timer) clearTimeout(chord.current.timer);
          if (hit.code) setTool(hit.code);
          if (hit.wait) {
            chord.current.seq = used;
            chord.current.timer = setTimeout(clearChord, MARK_CHORD_MS);
            return;
          }
          clearChord();
          return;
        }
      }

      if (dialog) return;
      // Цифри лишаються запасним способом: 1 — перша кнопка палітри, і так далі.
      const ix = Number(e.key) - 1;
      if (ix >= 0 && ix < codes.length) {
        clearChord();
        setTool(codes[ix]!.code);
        return;
      }
      if (mode === 'meals' && (e.key === '0' || e.key.toLowerCase() === 'g')) {
        clearChord();
        setHintMode(h => !h);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearChord();
    };
  }, [undoMarks, redoMarks, settings, mode, dialog]);

  const dates = useMemo<IsoDate[]>(() => {
    const n = daysInMonth(month.y, month.m);
    const from = Math.max(1, range.from ?? 1);
    const to = Math.min(n, range.to ?? n);
    const out: IsoDate[] = [];
    for (let d = from; d <= to; d++) out.push(iso(new Date(month.y, month.m, d)));
    return out;
  }, [month, range]);

  const command = useMemo(() => commandOrder(units, ranks), [units, ranks]);

  const visible = useMemo(() => {
    const q = searchKey(query);
    const shown = people.filter(p => {
      if (!p.active) return false;
      // Категорія лише звужує список, а пошук працює всередині неї: шаблон
      // харчування раніше повертав відповідь одразу, і пошук його не бачив.
      if (unitId.startsWith('__pattern:')) {
        if (p.patternId !== unitId.slice(10)) return false;
      } else if (unitId === '__sergeants') {
        if (!command.has(p)) return false;
      } else if (unitId === '__female') {
        if (!isFemale(p)) return false;
      } else if (unitId && p.unitId !== unitId) return false;
      if (!q) return true;
      const unit = units.find(u => u.id === p.unitId)?.name ?? '';
      return searchKey(`${p.lastName}${p.firstName}${p.patronymic}${p.taxCode}${unit}`).includes(q);
    });
    // Сержанти — за посадою, а не за абеткою: КГ, КВ-1, КВ-2, далі решта.
    return unitId === '__sergeants' ? shown.sort(command.sort) : shown;
  }, [people, units, command, query, unitId]);

  const isCurrentMonth = month.y === now.getFullYear() && month.m === now.getMonth();

  const shiftMonth = (delta: number): void => {
    const d = new Date(month.y, month.m + delta, 1);
    setMonth({ y: d.getFullYear(), m: d.getMonth() });
  };

  const goToday = (): void => {
    const t = new Date();
    setMonth({ y: t.getFullYear(), m: t.getMonth() });
    setRange({ from: null, to: null });
    setMonthPop(false);
    setMenu(false);
  };

  /**
   * Сам календар: рік зі стрілками, дванадцять місяців і «поточний».
   *
   * Один на дві оправи — на моніторі він розкривається з напису місяця, на
   * телефоні стоїть просто в меню. Вибір місяця там закриває меню: по місяць
   * туди й заходили, а таблиця за ним.
   */
  const monthCalendar = (
    <>
      <div className="mpop-head">
        <button onClick={() => setPopYear(y => y - 1)}>‹</button>
        <span>{popYear}</span>
        <button onClick={() => setPopYear(y => y + 1)}>›</button>
      </div>
      <div className="mpop-grid">
        {monthNames(settings.locale).map((name, i) => (
          <button
            key={name}
            className={`${popYear === month.y && i === month.m ? 'cur' : ''} ${
              popYear === now.getFullYear() && i === now.getMonth() ? 'now' : ''
            }`}
            onClick={() => {
              setMonth({ y: popYear, m: i });
              setMonthPop(false);
              setMenu(false);
            }}
          >
            {name.slice(0, 3)}
          </button>
        ))}
      </div>
      <button className="mpop-today" onClick={goToday}>
        Поточний місяць
      </button>
    </>
  );

  /**
   * Масовий перемикач із шапки: якщо в усіх стоїть — знімаємо, інакше ставимо.
   *
   * Обробники таблиці сталі: рядки перемальовуються лише тоді, коли справді
   * змінилися, а не тому, що сторінка віддала їм нову функцію.
   */
  const bulk = useCallback(
    (days: IsoDate[], meals: number[]): void => {
      const allSet = visible.every(p =>
        days.every(d => meals.every(mi => markIndex.get(p.id, d, mi) === tool)),
      );
      const ops: MarkOp[] = [];
      for (const p of visible) {
        for (const d of days) {
          for (const mi of meals) {
            ops.push({
              personId: p.id,
              date: d,
              meal: mi,
              kind: allSet ? null : tool,
              layer: 'main',
            });
          }
        }
      }
      applyMarks(ops);
    },
    [visible, markIndex, applyMarks, tool],
  );

  /** Рядкові кнопки: підтвердити або прибрати підказки за видимий період. */
  const hints = useCallback(
    (p: Person, action: 'confirm' | 'clear'): void => {
      const pm = markIndex.person(p.id);
      if (!pm) return;
      const ops: MarkOp[] = [];
      for (const d of dates) {
        for (let mi = 0; mi < settings.meals.length; mi++) {
          const hint = pm.hint.get(cellKey(d, mi));
          if (!hint) continue;
          ops.push({ personId: p.id, date: d, meal: mi, kind: null, layer: 'hint' });
          if (action === 'confirm')
            ops.push({ personId: p.id, date: d, meal: mi, kind: hint, layer: 'main' });
        }
      }
      if (!ops.length) {
        toast('Підказок немає');
        return;
      }
      applyMarks(ops);
      toast(
        action === 'confirm'
          ? `Підтверджено: ${ops.length - (ops.length >> 1)}`
          : `Прибрано підказок: ${ops.length}`,
      );
    },
    [markIndex, applyMarks, dates, settings.meals.length],
  );

  const openPerson = useCallback((p: Person): void => {
    setPerson(p);
    setDialog('report');
  }, []);

  // На телефоні лінійки під курсором немає; об'єкт мусить бути сталим, інакше
  // кожен рядок таблиці вважатиме, що вигляд змінився.
  const gridView = useMemo<ViewOptions>(
    () => (phone ? { ...view, crosshair: false } : view),
    [phone, view],
  );

  const markType = settings.markTypes.find(t => t.code === tool);
  const toolStyle = markType
    ? ({
        ['--tool-bg']: `var(--mk-${markType.code}-bg)`,
        ['--tool-fg']: `var(--mk-${markType.code}-fg)`,
      } as React.CSSProperties)
    : undefined;

  const actions: Array<{ label: string; run: () => void; primary?: boolean }> = [
    // Наряди, відпустки й ЛР самі в харчування не потрапляють: діловод
    // переносить їх, коли місяць розставлений, і обирає, які саме.
    ...(canEdit && mode === 'meals' ? [{ label: 'Перенесення', run: () => setDialog('transfer') }] : []),
    // У харчуванні «Заповнити» ставить за шаблонами, у нарядах розбирає
    // вставлений список чергувань. Відпустки заповнювати нема чим.
    ...(canEdit && (mode === 'meals' || mode === 'duty')
      ? [{ label: 'Заповнити', run: () => setDialog('fill') }]
      : []),
  ];

  /**
   * Меню «Фільтр»: групи та ролі за столом («кияни», «у розташуванні»).
   * Порожнє значення означає «усі».
   */
  const active = people.filter(p => p.active);
  const women = active.filter(isFemale).length;
  const filterItems: Array<{ value: string; label: string; count: number }> = [
    { value: '', label: 'Усі', count: active.length },
    ...units.map(u => ({
      value: u.id,
      label: u.name,
      count: active.filter(p => p.unitId === u.id).length,
    })),
    ...settings.mealPatterns.map(mp => ({
      value: `__pattern:${mp.id}`,
      label: mp.name,
      count: active.filter(p => p.patternId === mp.id).length,
    })),
    { value: '__sergeants', label: 'Сержанти', count: active.filter(command.has).length },
    // Дівчат часто треба бачити окремо — списки для них складають свої.
    ...(women ? [{ value: '__female', label: 'Дівчата', count: women }] : []),
  ];
  const filterLabel = filterItems.find(i => i.value === unitId)?.label ?? 'Фільтр';

  /** Що віддає документ або таблицю назовні: на телефоні йде в меню дій. */
  const fileItems: Array<{ label: string; run: () => void }> = [
    ...(settings.features.canteenList
      ? [{ label: 'Розхід', run: () => setDialog('canteen') }]
      : []),
    ...(mode === 'duty' ? [{ label: 'Хто наряд', run: () => setDialog('dutyToday') }] : []),
    ...(mode === 'meals' && canDocs
      ? [{ label: 'Перевірка', run: () => setDialog('check') }]
      : []),
  ];

  /* На телефоні «Розхід» і «Заповнити» з панелі не влазять — обидва списки
     збираються в меню однією стрічкою дій. */
  const menuActions = [...actions, ...fileItems];

  const palette = paletteOf(mode);

  // Рапорт — не позначка з налаштувань, а запис на дату, тож і кнопки в нього
  // сталі: «р» — звичайний, «д» — добовий. Кольори ті самі, що в клітинках.
  const reportTool = (
    <Segments className="tools">
      {(Object.keys(REPORT_KINDS) as ReportKind[]).map(kind => (
        <button
          key={kind}
          className={reportKind === kind ? 'on' : ''}
          style={
            { ['--tool-bg']: REPORT_KINDS[kind].bg, ['--tool-fg']: REPORT_KINDS[kind].fg } as React.CSSProperties
          }
          title={`${REPORT_KINDS[kind].label}: клік по дню ставить або знімає`}
          onClick={() => setReportKind(kind)}
        >
          {REPORT_KINDS[kind].letter}
        </button>
      ))}
    </Segments>
  );

  /* Замок позначок: той самий вигляд, що в замка минулих днів і ока підказки —
     кнопка в сегменті, а стан, що обмежує, червоний. Телефон тримає його в
     доку, комп'ютер — на панелі над таблицею. */
  const markLockTool = canEdit ? (
    <Segments className="tools">
      <button
        className={`ico-btn${markLock ? ' on' : ''}`}
        style={
          markLock
            ? ({
                ['--tool-bg']: 'var(--danger)',
                ['--tool-fg']: '#fff',
              } as React.CSSProperties)
            : undefined
        }
        aria-pressed={markLock}
        title={
          markLock
            ? phone
              ? 'Позначки заблоковано: тап нічого не змінює. Натисніть, щоб дозволити правки'
              : 'Позначки заблоковано: клік нічого не змінює. Натисніть, щоб дозволити правки'
            : phone
              ? 'Позначки ставляться тапом. Натисніть, щоб заблокувати'
              : 'Позначки ставляться кліком. Натисніть, щоб заблокувати'
        }
        onClick={() => setMarkLock(v => !v)}
      >
        <TouchIcon locked={markLock} />
      </button>
    </Segments>
  ) : null;

  const tools = mode === 'reports' ? (
    canEdit ? (
      <>
        {reportTool}
        {phone ? null : markLockTool}
      </>
    ) : null
  ) : canEdit ? (
    <>
      <Segments className={`tools${hintMode ? ' ghosting' : ''}`} style={toolStyle}>
        {palette.map((t, i) => (
          <button
            key={t.code}
            className={t.code === tool ? 'on' : ''}
            style={
              {
                ['--tool-bg']: `var(--mk-${t.code}-bg)`,
                ['--tool-fg']: `var(--mk-${t.code}-fg)`,
              } as React.CSSProperties
            }
            title={`${t.label} (${t.letter} або ${i + 1})`}
            onClick={() => setTool(t.code)}
          >
            {t.letter}
          </button>
        ))}
      </Segments>
      <Segments className="tools" hidden={mode !== 'meals'}>
        <button
          className={`ico-btn${hintMode ? ' on' : ''}`}
          style={
            hintMode
              ? ({
                  ['--tool-bg']: 'var(--accent)',
                  ['--tool-fg']: 'var(--accent-text)',
                } as React.CSSProperties)
              : undefined
          }
          title="Режим підказки: напівпрозорі позначки, які не рахуються (0)"
          onClick={() => setHintMode(h => !h)}
        >
          <HintIcon on={hintMode} />
        </button>
      </Segments>
      <Segments className="tools">
        {/* Замок минулих днів: закритий за замовчуванням, знімається на час
            роботи з минулим і сам повертається після перезавантаження.
            Закритий — червона кнопка, бо це стан, що обмежує; відкритий —
            звичайна сіра. */}
        <button
          className={`ico-btn${lockOpen ? '' : ' on'}`}
          style={
            lockOpen
              ? undefined
              : ({
                  ['--tool-bg']: 'var(--danger)',
                  ['--tool-fg']: '#fff',
                } as React.CSSProperties)
          }
          title={
            lockOpen
              ? 'Минулі дні відкриті для правок. Натисніть, щоб закрити'
              : 'Минулі дні закриті. Натисніть, щоб зняти замок і виправити їх'
          }
          onClick={() => setLockOpen(!lockOpen)}
        >
          <LockIcon open={lockOpen} />
        </button>
      </Segments>
      {/* На комп'ютері замок позначок — поруч із замком минулих днів: обидва
          про те, що можна правити. На телефоні він у доку. */}
      {phone ? null : markLockTool}
    </>
  ) : null;

  return (
    <div className={`sheet${history && mode !== 'reports' ? ' has-history' : ''}`}>
      <MarkStyles types={settings.markTypes} />

      <div ref={toolbar.ref} className={`toolbar balanced${toolbar.stacked ? ' stacked' : ''}`}>
        <div className="toolbar-left">
          <div className="search">
            <SearchIcon />
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              placeholder="Пошук"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="viewmenu" ref={filterBox}>
            <button
              className={`btn sm${unitId ? ' primary' : ''}`}
              aria-haspopup="menu"
              aria-expanded={filterPop}
              onClick={() => setFilterPop(v => !v)}
            >
              {filterLabel}
            </button>
            {filterPop ? (
              <div className="vpop" role="menu">
                {filterItems.map(item => (
                  <button
                    key={item.value}
                    className={`vpop-row${unitId === item.value ? ' on' : ''}`}
                    onClick={() => {
                      setUnitId(item.value);
                      setFilterPop(false);
                    }}
                  >
                    {item.label}
                    <span className="vpop-count">{item.count}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* На телефоні місяць переїхав у меню під «☰»: у панелі він з'їдав
            рядок, а календар, що розкривався поверх таблиці, накривав саме те,
            заради чого його й відкривали. */}
        {phone ? null : (
          <div className="toolbar-mid">
            {/* Стрілки й місяць — три окремі кнопки. У спільній коробці стрілки
                читалися як частина напису, і не було видно, що натиснути можна
                й сам місяць: він відкриває календар. */}
            <div className="month" ref={monthBox}>
              <button
                className="btn sm month-step"
                onClick={() => shiftMonth(-1)}
                title="Попередній місяць"
              >
                ‹
              </button>
              <button
                className={`btn sm mlabel${isCurrentMonth ? ' now' : ''}`}
                title={isCurrentMonth ? 'Поточний місяць' : undefined}
                onClick={() => setMonthPop(v => !v)}
              >
                {monthTitle(month.y, month.m, settings.locale)}
              </button>
              <button
                className="btn sm month-step"
                onClick={() => shiftMonth(1)}
                title="Наступний місяць"
              >
                ›
              </button>
              {monthPop ? <div className="mpop">{monthCalendar}</div> : null}
            </div>
          </div>
        )}

        <div className="toolbar-right desk-only">
          {tools}
          {mode === 'duty' ? (
            <button className="btn sm" onClick={() => setDialog('dutyToday')}>
              Хто наряд
            </button>
          ) : null}
          {mode === 'meals' && canDocs ? (
            <button
              className={`btn sm${issues ? ' on' : ''}`}
              title="Звірити рапорти, таблицю й розхід за місяць"
              onClick={() => setDialog('check')}
            >
              Перевірка
            </button>
          ) : null}
          {/* «Розхід» одразу відкриває список ПЗ — меню з одного пункту зайве. */}
          {settings.features.canteenList ? (
            <button className="btn sm" onClick={() => setDialog('canteen')}>
              Розхід
            </button>
          ) : null}
          <div className="viewmenu" ref={viewBox}>
            <button
              className={`btn sm${viewPop ? ' on' : ''}`}
              aria-haspopup="menu"
              aria-expanded={viewPop}
              onClick={() => setViewPop(v => !v)}
            >
              Вигляд
            </button>
            {viewPop ? (
              <div className="vpop right toggles" role="menu">
                {/* Дні місяця — найперше в меню: цим полем звужують таблицю до
                    потрібного тижня частіше, ніж чіпають будь-який перемикач,
                    а лежало воно аж під ними. */}
                <div className="vpop-inline">
                  <span className="vpop-row vpop-static">Показувати дні місяця</span>
                  <div className="vpop-tail">
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={31}
                      placeholder="1"
                      value={range.from ?? ''}
                      onChange={e =>
                        setRange(r => ({
                          ...r,
                          from: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    />
                    <span>—</span>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={31}
                      placeholder={String(daysInMonth(month.y, month.m))}
                      value={range.to ?? ''}
                      onChange={e =>
                        setRange(r => ({
                          ...r,
                          to: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="vpop-sep" />
                {/* Рядок сам є кнопкою, а смужка в ньому — лише вигляд: кнопка
                    всередині кнопки неприпустима, та й попадати мишею в саму
                    смужку не треба — вмикає весь рядок. */}
                {VIEW_ITEMS.map(item => (
                  <button
                    key={item.key}
                    type="button"
                    className="vpop-row"
                    role="menuitemcheckbox"
                    aria-checked={view[item.key]}
                    onClick={() => toggleView(item.key)}
                  >
                    <span className={`switch${view[item.key] ? ' on' : ''}`} aria-hidden="true">
                      <i />
                    </span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {mode !== 'reports' ? (
            <button
              className={`btn sm${history ? ' on' : ''}`}
              title="Хто, коли й що змінював у таблиці за цей місяць"
              onClick={() => (history ? closeHistory() : setHistory(true))}
            >
              Історія
            </button>
          ) : null}
          {actions.map(a => (
            <button
              key={a.label}
              className={`btn sm ${a.primary ? 'primary' : ''}`}
              onClick={a.run}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'reports' ? (
        <LeaveReportsGrid
          people={visible}
          dates={dates}
          ranks={ranks}
          units={units}
          positions={positions}
          patterns={settings.mealPatterns}
          locale={settings.locale}
          view={gridView}
          readOnly={!canEdit || markLock}
          onLocked={canEdit && markLock ? warnMarkLock : undefined}
          tool={reportKind}
        />
      ) : (
        <Grid
          people={visible}
          dates={dates}
          settings={settings}
          ranks={ranks}
          units={units}
          positions={positions}
          tool={tool}
          hintMode={hintMode}
          mode={mode}
          view={gridView}
          recent={recentKeys}
          recentGone={recentGone}
          issues={mode === 'meals' ? (issues ?? undefined) : undefined}
          tapCells={phone}
          readOnly={!canEdit || markLock}
          onLocked={canEdit && markLock ? warnMarkLock : undefined}
          onBulk={bulk}
          onHints={hints}
          onPersonClick={openPerson}
        />
      )}

      {phone ? (
        /* Док — це палітра позначок під великим пальцем, і тільки вона. «☰»
           звідси прибрано: меню відкривається з шапки, однією й тією самою
           кнопкою на всіх сторінках. */
        <div className="dock">
          {markLockTool}
          {tools}
        </div>
      ) : null}

      {menu ? (
        /* Кнопка меню живе в шапці, а вміст — тут: до спільних рядків таблиця
           додає свої «Вигляд» і «Дії». Тому стан відкритості спільний. */
        <MobileMenu
          onClose={() => setMenu(false)}
          extra={
            <>
              {/* Місяць — перший блок: із меню найчастіше йдуть саме по нього. */}
              <div className="mmenu-block">
                <div className="mmenu-title">Місяць</div>
                {/* Те саме, що в панелі таблиці на моніторі: стрілки по боках, а
                    посередині місяць — залитий, коли він поточний. Тап по ньому
                    розкриває сітку місяців і року. */}
                <div className="month mmenu-month">
                  <button className="btn month-step" onClick={() => shiftMonth(-1)} title="Попередній місяць">
                    ‹
                  </button>
                  <button
                    className={`btn mlabel${isCurrentMonth ? ' now' : ''}`}
                    aria-expanded={menuGrid}
                    onClick={() => setMenuGrid(v => !v)}
                  >
                    {monthTitle(month.y, month.m, settings.locale)}
                  </button>
                  <button className="btn month-step" onClick={() => shiftMonth(1)} title="Наступний місяць">
                    ›
                  </button>
                </div>
                {menuGrid ? <div className="mmenu-cal">{monthCalendar}</div> : null}
              </div>

              {menuActions.length ? (
                <div className="mmenu-block">
                  <div className="mmenu-title">Дії</div>
                  {menuActions.map(a => (
                    <button
                      key={a.label}
                      type="button"
                      className="mmenu-row"
                      onClick={() => {
                        setMenu(false);
                        a.run();
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {/* «Вигляд» з панелі: на телефоні зайві стовпці заважають ще
                  дужче, ніж на моніторі. Лінійки під курсором у списку немає —
                  на сенсорному екрані вона вимкнена завжди. */}
              <div className="mmenu-block">
                <div className="mmenu-title">Вигляд</div>
                <div className="mmenu-field">
                  <span>Показувати дні місяця</span>
                  <div className="mmenu-inline">
                    <input
                      className="input"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={31}
                      placeholder="1"
                      value={range.from ?? ''}
                      onChange={e =>
                        setRange(r => ({
                          ...r,
                          from: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    />
                    <span>—</span>
                    <input
                      className="input"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={31}
                      placeholder={String(daysInMonth(month.y, month.m))}
                      value={range.to ?? ''}
                      onChange={e =>
                        setRange(r => ({
                          ...r,
                          to: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    />
                  </div>
                </div>
                {VIEW_ITEMS.filter(item => item.key !== 'crosshair').map(item => (
                  <button
                    key={item.key}
                    type="button"
                    className="mmenu-row"
                    role="menuitemcheckbox"
                    aria-checked={view[item.key]}
                    onClick={() => toggleView(item.key)}
                  >
                    <span className={`switch${view[item.key] ? ' on' : ''}`} aria-hidden="true">
                      <i />
                    </span>
                    <span>{item.label}</span>
                  </button>
                ))}
                {mode !== 'reports' ? (
                  <button
                    type="button"
                    className="mmenu-row"
                    onClick={() => {
                      setMenu(false);
                      setHistory(true);
                    }}
                  >
                    <span>Історія змін</span>
                    <span className="mmenu-go" aria-hidden="true">
                      ›
                    </span>
                  </button>
                ) : null}
              </div>
            </>
          }
        />
      ) : null}

      {history && mode !== 'reports' ? (
        <HistoryPanel
          layers={mode === 'meals' ? ['main', 'hint'] : mode === 'duty' ? ['duty'] : ['away']}
          from={monthFrom}
          to={monthTo}
          people={people}
          types={settings.markTypes}
          reloadKey={syncing}
          onHighlight={highlight}
          onClose={closeHistory}
        />
      ) : null}
      {dialog === 'transfer' ? (
        <TransferDialog
          people={people}
          dates={dates}
          min={monthFrom}
          max={monthTo}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'fill' ? (
        <FillDialog
          part={mode === 'duty' ? 'duties' : 'meals'}
          from={dates[0]!}
          to={dates[dates.length - 1]!}
          year={month.y}
          month={month.m + 1}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'canteen' ? <CanteenModal onClose={() => setDialog(null)} /> : null}
      {dialog === 'dutyToday' ? <DutyTodayModal onClose={() => setDialog(null)} /> : null}
      {dialog === 'check' ? (
        <CheckModal
          from={monthFrom}
          to={monthTo}
          monthLabel={monthTitle(month.y, month.m, settings.locale)}
          result={check}
          busy={checking}
          highlighted={checkShown}
          onRun={() => void runCheck()}
          onHighlight={setCheckShown}
          onShow={showIssue}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'report' ? (
        <ReportModal
          person={person}
          from={dates[0]!}
          to={dates[dates.length - 1]!}
          onClose={() => {
            setDialog(null);
            setPerson(null);
          }}
        />
      ) : null}
    </div>
  );
}
